import {
  APPLICATION_DOCUMENT_ACCEPTED_FILES,
} from 'config/constants'
import { formatReferenceCopy } from 'utils/typography'
import React, {
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  FileUpload,
  Check,
} from '@vedmarket/ui-kit'

import ModalBig from '../../../../common/modalBig'

const CheckItemCompleteBlockModal = ({
  open,
  item,
  runAction,
  onClose,
  onUploadDocument,
  onCompleteBlock,
}: ApplicationCheckItemCompleteBlockModalProps) => {
  const { t } = useTranslation()

  const [ comment, setComment ] = useState('')
  const [ pendingFiles, setPendingFiles ] = useState<File[]>([])

  const handleClose = () => {
    setComment('')
    setPendingFiles([])
    onClose()
  }

  const handleFiles = (files: File[]) => {
    setPendingFiles((current) => [ ...current, ...files ])
    return false
  }

  const removePendingFile = (index: number) => {
    setPendingFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))
  }

  const handleSubmit = () => {
    if (!item) return
    runAction(async () => {
      for (const file of pendingFiles) {
        await onUploadDocument({ kind: item.kind, file })
      }
      await onCompleteBlock({
        kind: item.kind,
        manager_note: comment.trim(),
      })
      handleClose()
    }, 'applications__message__checkCompleted')
  }

  if (!item) {
    return null
  }

  const isCompanyCheck = item.kind === 'company_info'

  return (
    <ModalBig
      isOpen={open}
      onClose={handleClose}
      title={formatReferenceCopy(t(isCompanyCheck
        ? 'applications__text__blockCompleteCompanyModalTitle'
        : 'applications__text__blockCloseCheck'))}
      width={736}
      buttons={(
        <Stack direction='row' spacing={3} sx={{ marginTop: 3, width: '100%' }}>
          <Button
            variant='outlined'
            sx={{ minWidth: '238px', flexShrink: 0 }}
            onClick={handleClose}
          >
            <Typography component='span' variant='inherit'>
              {formatReferenceCopy(t('actions__text__cancel'))}
            </Typography>
          </Button>
          <Button variant='contained' fullWidth startIcon={<Check />} onClick={handleSubmit}>
            <Typography component='span' variant='inherit'>
              {formatReferenceCopy(t('applications__text__blockCloseCheck'))}
            </Typography>
          </Button>
        </Stack>
      )}
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          {!isCompanyCheck && <Typography variant='h6'>{item.kind_label}</Typography>}
          <Typography variant='body2' color='text.secondary'>
            {formatReferenceCopy(t('applications__text__blockCompleteModalDocumentsHint'))}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Typography variant='body2' color='text.secondary'>
            {formatReferenceCopy(t('applications__text__blockCompleteModalDocumentsTitle'))}
          </Typography>
          {pendingFiles.map((file, index) => (
            <FileUpload
              key={`${file.name}-${file.lastModified}-${index}`}
              acceptedFilesConfig={APPLICATION_DOCUMENT_ACCEPTED_FILES}
              initialFileTitle={file.name}
              onRemove={() => removePendingFile(index)}
            />
          ))}

          <FileUpload
            key={`block-attachment-${pendingFiles.length}`}
            multiple
            acceptedFilesConfig={APPLICATION_DOCUMENT_ACCEPTED_FILES}
            onHandleFiles={handleFiles}
          />
        </Stack>
        <TextField
          fullWidth
          size='small'
          multiline
          minRows={3}
          InputLabelProps={{ shrink: true }}
          label={<Typography component='span' variant='inherit'>{formatReferenceCopy(t('applications__text__blockCompleteModalComment'))}</Typography>}
          placeholder={formatReferenceCopy(t('applications__text__blockCompleteModalCommentPlaceholder'))}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Stack>
    </ModalBig>
  )
}

export default CheckItemCompleteBlockModal
